import {
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@cmg/design-system';
import { FormattedTime, useIntl } from '@cmg/i18n';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useLineupMatchingCmgEntityKeys } from '../../../../../../../../common/hooks/useMatchingCmgEntityKeys';
import {
  InstitutionalIndicationOrderType,
  OfferingType,
  SecurityType,
} from '../../../../../../../../graphql';
import { getInstrumentName } from '../../../../../../../offering-setup-next/utils';
import { institutionalIndicationOrderTypeLabels } from '../../../../../../../order-book/constants';
import {
  SyndicateIndication_PublishedOfferings_InstrumentPartsFragment,
  SyndicateIndication_PublishedOfferings_Terms_FilingsPartsFragment,
} from '../../../../context/hooks/syndicate/__generated__';
import {
  IndicationForm_IndicationDetails_Offering_OrderEntryProfilePartsFragment,
  IndicationForm_IndicationDetails_OfferingPartsFragment,
} from '../../../indication-form/components/indication-details/graphql/__generated__';
import { IndicationForm_MyIndicationPartsFragment } from '../../../indication-form/graphql/__generated__';
import GridRowPair from '../indication-section-read-view/grid-row-pair/GridRowPair';
import InterestLevelsHeaderRow from '../indication-section-read-view/interest-levels-header-row/InterestLevelsHeaderRow';
import AcknowledgedByList from './acknowledged-by-list/AcknowledgedByList';
import {
  ACK_BY_TOOLTIP_TEXT,
  getDemandUnitLabel,
  getFirms,
  getInterestQuantityCellValue,
  getLimitTypeCellValue,
} from './IndicationSectionReadContent.model';
import { InterestLevelsTableReadContentConvertibles } from './InterestLevelsTableReadContentConvertibles';

export type Props = Readonly<{
  offeringType: OfferingType | null;
  submittedOn: string | null | undefined;
  demandInstruments: Readonly<
    SyndicateIndication_PublishedOfferings_InstrumentPartsFragment[]
  > | null;
  acknowledgements: IndicationForm_MyIndicationPartsFragment['acknowledgements'];
  indicationVersion: string;
  indicationInstrumentId: string | null | undefined;
  indicationTrancheId: string | null | undefined;
  managers: IndicationForm_IndicationDetails_OfferingPartsFragment['syndicate']['managers'];
  bndAgentCmgEntityKey: string | null | undefined;
  interestLevels: IndicationForm_MyIndicationPartsFragment['interestLevels'];
  tranches: IndicationForm_IndicationDetails_Offering_OrderEntryProfilePartsFragment['tranches'];
  // this is demand currency
  demandCurrencyCode: string | null | undefined;
  securityTypeDisplayName: string;
  securityType: SecurityType | null;
  filings: Readonly<SyndicateIndication_PublishedOfferings_Terms_FilingsPartsFragment[]> | null;
  isIndicationPriceAsPercentageParEnabled: boolean;
  issuerName: string | null;
}>;
const IndicationSectionReadContent: React.FC<Props> = ({
  offeringType,
  submittedOn,
  demandInstruments,
  acknowledgements,
  indicationVersion,
  managers,
  bndAgentCmgEntityKey,
  interestLevels,
  tranches,
  indicationTrancheId,
  demandCurrencyCode,
  indicationInstrumentId,
  securityTypeDisplayName,
  filings,
  isIndicationPriceAsPercentageParEnabled,
  securityType,
  issuerName,
}) => {
  const matchingCmgEntityKeys = useLineupMatchingCmgEntityKeys(managers);
  const intl = useIntl();

  const tranche = tranches.find(({ id }) => id === indicationTrancheId)!;
  const firms = getFirms(acknowledgements, managers, matchingCmgEntityKeys, indicationVersion);
  const bndAgentName = managers.find(
    ({ cmgEntityKey }) => bndAgentCmgEntityKey === cmgEntityKey
  )?.firmName;
  const demandInstrument = demandInstruments?.find(({ id }) => id === indicationInstrumentId);

  return (
    <Stack gap={1}>
      <Grid container rowGap={1}>
        <GridRowPair
          label="Submitted On:"
          value={submittedOn}
          formatter={value => <FormattedTime value={value} format="dateTime" />}
        />
        <GridRowPair label="Tranche:" value={tranche.name} />
        <GridRowPair
          label="Demand Instrument:"
          value={defaultTo(
            demandInstrument?.name,
            getInstrumentName({
              issuerName,
              securityTypeDisplayName,
              stockSymbol: demandInstrument?.stockSymbol ?? null,
              isDepositaryReceipt: !!demandInstrument?.isDepositaryReceipt,
            })
          )}
        />
        <GridRowPair label="Demand Currency:" value={demandCurrencyCode} />
        <GridRowPair
          label={
            <Tooltip variant="info" title={ACK_BY_TOOLTIP_TEXT} placement="top-start">
              <Typography color="text.secondary">Acknowledged by ⓘ:</Typography>
            </Tooltip>
          }
          value={<AcknowledgedByList values={firms} />}
        />
        <GridRowPair label="Billing & Delivery Agent:" value={bndAgentName} />
      </Grid>

      <div>
        <Typography variant="h4" paddingY={1}>
          Interest Levels
        </Typography>
        {offeringType === OfferingType.Convertible ? (
          <InterestLevelsTableReadContentConvertibles
            interestLevels={interestLevels}
            filings={filings}
            showPercentageOfPar={isIndicationPriceAsPercentageParEnabled}
            securityType={securityType}
          />
        ) : (
          <TableContainer>
            <Table data-testid="interest-levels-table" sx={{ width: 650 }}>
              <TableHead>
                <InterestLevelsHeaderRow />
              </TableHead>

              <TableBody>
                {interestLevels.map(
                  ({ interestQuantity, interestUnit, limitPrice, limitType }, index) => {
                    const orderType = limitPrice
                      ? InstitutionalIndicationOrderType.Limit
                      : InstitutionalIndicationOrderType.Market;

                    return (
                      <TableRow key={index}>
                        <TableCell
                          size="medium"
                          align="right"
                          data-testid="interest-levels-quantity-readonly"
                        >
                          {getInterestQuantityCellValue({
                            interestUnit,
                            interestQuantity,
                            demandCurrency: demandCurrencyCode,
                            intl,
                          })}
                        </TableCell>
                        <TableCell size="medium" data-testid="interest-levels-unit-readonly">
                          {getDemandUnitLabel(
                            interestUnit,
                            securityType,
                            !!demandInstrument?.isDepositaryReceipt
                          )}
                        </TableCell>
                        <TableCell size="medium" data-testid="interest-levels-order-type-readonly">
                          {institutionalIndicationOrderTypeLabels[orderType]}
                        </TableCell>
                        <TableCell size="medium" data-testid="interest-levels-limit-readonly">
                          {getLimitTypeCellValue({
                            orderType,
                            limitType,
                            currency: demandCurrencyCode,
                            limitPrice,
                            intl,
                          })}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </Stack>
  );
};

export default IndicationSectionReadContent;
